import { apiSlice } from './apiSlice'

export enum DocumentType {
  CASE_DOCUMENT = 'case_document',
  REFERENCE = 'reference',
  MEMORIAL = 'memorial',
  EVIDENCE = 'evidence',
  TRANSCRIPT = 'transcript',
  RUBRIC = 'rubric',
}

export enum DocumentStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  FAILED = 'failed',
}

/** BTC tag pool documents để chấm tự động phần Chứng cứ (rubric Phần 2). */
export enum EvidenceLabel {
  CORRECT = 'correct',
  DECOY = 'decoy',
}

export interface Document {
  id: string
  title: string
  description?: string
  document_type: DocumentType
  case_id?: string
  team_id?: string
  uploaded_by: string
  storage_path: string
  file_name: string
  file_size: number
  mime_type: string
  /** true khi file lưu ciphertext (đề/reference/rubric) — BR-02-01 */
  storage_encrypted?: boolean
  status: DocumentStatus
  chunk_count: number
  embedding_model?: string
  processed_at?: string
  created_at?: string
  updated_at?: string
  /** BTC gán: correct | decoy | null (null = chưa phân loại). Đội thi luôn nhận null từ pool. */
  evidence_label?: EvidenceLabel | null
}

export interface DocumentCreate {
  title: string
  description?: string
  document_type: DocumentType
  case_id?: string
  team_id?: string
}

export interface DocumentSearchQuery {
  query: string
  case_id?: string
  team_id?: string
  document_type?: DocumentType
  limit?: number
  similarity_threshold?: number
}

/** Body cho POST /documents/search/scoring (RAG hỗ trợ chấm điểm) */
export interface DocumentSearchScoringQuery {
  query: string
  case_id: string
  team_id: string
  limit?: number
  similarity_threshold?: number
  case_weight?: number
  team_weight?: number
}

export interface DocumentSearchResult {
  chunk_id: string
  document_id: string
  document_title: string
  content: string
  similarity_score: number
  metadata?: Record<string, any>
}

export const documentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    uploadDocument: builder.mutation<Document, FormData>({
      query: (formData) => ({
        url: '/documents/create',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Document'],
    }),
    getDocuments: builder.query<Document[], { case_id?: string; team_id?: string; document_type?: string }>({
      query: (params) => ({
        url: '/documents/list',
        params,
      }),
      providesTags: ['Document'],
    }),
    getDocument: builder.query<Document, string>({
      query: (id) => `/documents/${id}`,
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    downloadDocument: builder.query<Blob, string>({
      query: (id) => ({
        url: `/documents/${id}/download`,
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const contentType = response.headers.get('content-type') || ''
            if (contentType.includes('application/json')) {
              try {
                return await response.json()
              } catch {
                return { detail: 'Download failed' }
              }
            }
            const text = await response.text().catch(() => '')
            return { detail: text || 'Download failed' }
          }
          return response.blob()
        },
      }),
    }),
    // Preview as PDF — BE convert Office docs sang PDF (cache content-addressed).
    downloadDocumentAsPdf: builder.query<Blob, string>({
      query: (id) => ({
        url: `/documents/${id}/download?as_pdf=1`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    processDocument: builder.mutation<{ message: string; status: string }, string>({
      query: (id) => ({
        url: `/documents/${id}/process`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    searchDocuments: builder.mutation<DocumentSearchResult[], DocumentSearchQuery>({
      query: (body) => ({
        url: '/documents/search',
        method: 'POST',
        body,
      }),
    }),
    /** RAG tìm kiếm hỗ trợ chấm điểm (case + team submissions, kết hợp case_weight/team_weight) */
    searchDocumentsScoring: builder.mutation<DocumentSearchResult[], DocumentSearchScoringQuery>({
      query: (body) => ({
        url: '/documents/search/scoring',
        method: 'POST',
        body,
      }),
    }),
    deleteDocument: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    updateDocument: builder.mutation<
      Document,
      {
        id: string
        title?: string
        description?: string
        evidence_label?: EvidenceLabel | null
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/documents/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Document', id }, 'Document'],
    }),
  }),
})

export const {
  useUploadDocumentMutation,
  useGetDocumentsQuery,
  useGetDocumentQuery,
  useLazyDownloadDocumentQuery,
  useLazyDownloadDocumentAsPdfQuery,
  useProcessDocumentMutation,
  useSearchDocumentsMutation,
  useSearchDocumentsScoringMutation,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
} = documentsApi
